import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { teamAPI } from '../services/api';
import { toast } from 'sonner';
import {
  Home, Calendar as CalendarIcon, Users, FileText, Heart, DollarSign,
  Scale, ShoppingCart, Activity, Flower, Award, BarChart3, LogOut,
  Menu, X, ChevronDown, UserPlus, CheckSquare
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { teams, currentTeam, switchTeam } = useTeam();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/medical', icon: Activity, label: 'Medical' },
    { path: '/financial', icon: DollarSign, label: 'Financial' },
    { path: '/legal', icon: Scale, label: 'Legal' },
    { path: '/household', icon: ShoppingCart, label: 'Household' },
    { path: '/caregiving', icon: Heart, label: 'Caregiving' },
    { path: '/end-of-life', icon: Flower, label: 'End of Life' },
    { path: '/memorial', icon: Flower, label: 'Memorial' },
    { path: '/gratitude', icon: Award, label: 'Gratitude' },
    { path: '/providers', icon: Users, label: 'Providers' },
    { path: '/documents', icon: FileText, label: 'Documents' },
    { path: '/calendar', icon: CalendarIcon, label: 'Calendar' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72
        glass-effect border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_pressed-collab/artifacts/sya4rwpg_Black%20Font%20Transp%20Bg.png" 
                alt="Pressed Logo" 
                className="h-14 w-auto"
              />
            </div>
            
            {/* Team Selector */}
            {currentTeam && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="team-selector">
                    <span className="truncate">{currentTeam.name}</span>
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64">
                  {teams.map(team => (
                    <DropdownMenuItem
                      key={team.id}
                      onClick={() => switchTeam(team.id)}
                      data-testid={`team-option-${team.id}`}
                    >
                      {team.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={() => navigate('/teams/create')} data-testid="create-team-btn">
                    + Create New Team
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {navItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl
                    transition-all duration-200
                    ${isActive(item.path)
                      ? 'nav-item-active'
                      : 'nav-item text-gray-700'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={user?.picture || 'https://via.placeholder.com/40'}
                alt={user?.name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            
            {/* Quick Invite Button */}
            {currentTeam && (
              <InviteMemberDialog teamId={currentTeam.id} />
            )}
            
            <Button
              variant="outline"
              className="w-full justify-start gap-2 mt-2"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden glass-effect border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X /> : <Menu />}
            </Button>
            <div className="flex items-center">
              {/* Icon version for mobile header - cleaner look */}
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-xl font-bold">P</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

const InviteMemberDialog = ({ teamId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('support_team');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await teamAPI.inviteMember(teamId, { email, role });
      toast.success('Invitation sent!');
      setOpen(false);
      setEmail('');
      setRole('support_team');
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error('Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-invite-btn">
          <UserPlus className="w-4 h-4" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>Invite someone to join your care team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="invite-email">Email Address *</Label>
            <Input
              id="invite-email"
              type="email"
              data-testid="invite-email-input"
              placeholder="friend@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="invite-role">Permission Level *</Label>
            <select
              id="invite-role"
              data-testid="invite-role-select"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="inner_circle">Inner Circle (Full Access - Family/Close Friends)</option>
              <option value="support_team">Support Team (Limited Access - Helpers)</option>
              <option value="provider">Provider (Professional - Healthcare/Legal)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Inner Circle sees everything. Support Team sees shared items only. Providers see professional info only.
            </p>
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="send-invite-btn"
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Layout;